import React from 'react';
import { IataRequirementsList } from '../../data/iataRules';
import { Link } from 'react-router-dom';
import { Plane, AlertTriangle, CheckCircle, ExternalLink } from 'lucide-react';
import { SEOLayout, AlertBox } from '../../components/SEOLayout';
import { allAirlinesData as airlinesData } from '../../data/seoDataMerged';
import { Button } from '../../components/ui/button';

const AirlinesHub = () => {
  const airlines = Object.values(airlinesData);
  
  // Group by region
  const europeanAirlines = airlines.filter(a => ['France', 'Germany', 'United Kingdom', 'Netherlands', 'Switzerland'].includes(a.country));
  const americanAirlines = airlines.filter(a => ['United States'].includes(a.country));
  const middleEastAirlines = airlines.filter(a => ['United Arab Emirates'].includes(a.country));
  const otherAirlines = airlines.filter(a => !['France', 'Germany', 'United Kingdom', 'Netherlands', 'Switzerland', 'United States', 'United Arab Emirates'].includes(a.country));

  const AirlineCard = ({ airline }) => (
    <Link 
      to={`/airlines/${airline.slug}`}
      className="block bg-white border border-slate-200 rounded-xl p-6 hover:shadow-lg hover:border-blue-300 transition-all"
    >
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">{airline.name}</h3>
          <p className="text-slate-500 text-sm">{airline.code} • {airline.country}</p>
        </div>
      </div>

      <div className="mt-4 text-blue-600 text-sm font-medium flex items-center gap-1">
        Official policy link & IATA rules <ExternalLink className="w-3 h-3" />
      </div>
    </Link>
  );

  const AirlineSection = ({ title, airlines }) => (
    <section className="mb-12">
      <h2 className="text-2xl font-bold text-slate-900 mb-6">{title}</h2>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {airlines.map(airline => (
          <AirlineCard key={airline.slug} airline={airline} />
        ))}
      </div>
    </section>
  );

  return (
    <SEOLayout
      title="Airline Pet Policies 2026 | Complete Guide to Flying with Pets"
      description="Complete guide to pet travel policies for all major airlines. Cabin rules, cargo requirements, breed restrictions, crate specifications. Updated 2026."
      keywords={['airline pet policy', 'flying with pets', 'pet cargo airlines', 'airline crate requirements', 'pet travel airlines']}
      breadcrumbs={[{ label: 'Airlines' }]}
    >
      {/* Hero */}
      <div className="text-center mb-12">
        <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
          <Plane className="w-4 h-4" />
          Updated for 2026
        </div>
        <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
          Airline Pet Travel Policies
        </h1>
        <p className="text-xl text-slate-600 max-w-3xl mx-auto">
          Every airline has different rules for flying with pets. Wrong crate? Banned breed? 
          Missing documents? You'll be <span className="text-red-600 font-semibold">rejected at check-in</span>. 
          Know the rules before you book.
        </p>
      </div>

      {/* Warning Box */}
      <div className="mb-12">
        <AlertBox type="danger" title="Don't Get Stranded at the Airport">
          <p>
            Each airline has specific requirements 
            for crate size, materials, ventilation, and documentation. Many ban certain breeds entirely from cargo. 
            Check your specific airline's policy AND verify your crate meets IATA standards.
          </p>
          <div className="mt-3">
            <Link to="/">
              <Button size="sm" className="bg-red-600 hover:bg-red-700">
                Pre-Check Your Crate Now
              </Button>
            </Link>
          </div>
        </AlertBox>
      </div>

      {/* Quick Stats */}
      <div className="grid md:grid-cols-4 gap-4 mb-12">
        <div className="bg-white border border-slate-200 rounded-xl p-4 text-center">
          <div className="text-2xl font-bold text-red-600">10-15%</div>
          <div className="text-slate-600 text-sm">Average crate rejection rate</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4 text-center">
          <div className="text-2xl font-bold text-amber-600">24+</div>
          <div className="text-slate-600 text-sm">Breeds banned from cargo</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4 text-center">
          <div className="text-2xl font-bold text-blue-600">$95-600</div>
          <div className="text-slate-600 text-sm">Typical pet travel fees</div>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4 text-center">
          <div className="text-2xl font-bold text-slate-900">48hrs</div>
          <div className="text-slate-600 text-sm">Minimum advance booking</div>
        </div>
      </div>

      {/* Airlines by Region */}
      {americanAirlines.length > 0 && (
        <AirlineSection title="🇺🇸 North American Airlines" airlines={americanAirlines} />
      )}
      
      {europeanAirlines.length > 0 && (
        <AirlineSection title="🇪🇺 European Airlines" airlines={europeanAirlines} />
      )}
      
      {middleEastAirlines.length > 0 && (
        <AirlineSection title="🌍 Middle East Airlines" airlines={middleEastAirlines} />
      )}
      
      {otherAirlines.length > 0 && (
        <AirlineSection title="🌏 Asia Pacific & Other Airlines" airlines={otherAirlines} />
      )}

      {/* Common Requirements Section */}
      <section className="mt-16 bg-slate-100 rounded-2xl p-8">
        <h2 className="text-2xl font-bold text-slate-900 mb-6">
          Universal IATA Crate Requirements (All Airlines)
        </h2>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <h3 className="font-semibold text-slate-900 mb-3">IATA container requirements</h3>
            <IataRequirementsList />
          </div>
          <div>
            <h3 className="font-semibold text-slate-900 mb-3">❌ Common Crate Problems</h3>
            <ul className="space-y-2 text-slate-700">
              <li>• Crate too small (most common)</li>
              <li>• Plastic clips instead of metal bolts</li>
              <li>• Insufficient ventilation</li>
              <li>• Soft-sided carrier for cargo</li>
              <li>• Missing or expired health certificate</li>
              <li>• Brachycephalic breed in cargo booking</li>
              <li>• Missing "Live Animal" labels</li>
              <li>• Temperature embargo (seasonal)</li>
            </ul>
          </div>
        </div>
      </section>

      {/* SEO Content */}
      <section className="mt-16">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600 mb-8">
            Flying with Pets: What You Need to Know in 2026
          </h2>
          
          <div className="space-y-6">
            <p className="text-lg text-slate-700 leading-relaxed">
              Each airline maintains strict policies governing 
              crate specifications, breed restrictions, and documentation requirements. Understanding these 
              rules before you book can save you from the devastating experience of being rejected at check-in.
            </p>
            
            <div className="bg-gradient-to-r from-slate-50 to-blue-50 rounded-xl p-6 border border-slate-100">
              <h3 className="text-xl font-bold text-slate-900 mb-3 flex items-center gap-2">
                <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                Cabin vs. Cargo: Understanding Your Options
              </h3>
              <p className="text-slate-600 leading-relaxed">
                Most airlines offer two options for pet travel: in-cabin (under your seat) and cargo hold. 
                Cabin travel is limited to small pets in soft-sided carriers, typically with a combined 
                weight limit of 8-10kg. Larger pets must travel in the cargo hold in IATA-compliant 
                rigid containers. Some airlines, like British Airways and Emirates, do not allow any pets 
                in the passenger cabin.
              </p>
            </div>

            <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-6 border border-amber-100">
              <h3 className="text-xl font-bold text-amber-800 mb-3 flex items-center gap-2">
                <span className="w-2 h-2 bg-amber-500 rounded-full"></span>
                The Brachycephalic Problem
              </h3>
              <p className="text-amber-700 leading-relaxed">
                Snub-nosed (brachycephalic) breeds face severe restrictions on nearly all airlines. 
                Breeds like French Bulldogs, English Bulldogs, Pugs, and Boston Terriers are banned from 
                cargo holds due to their compromised respiratory systems. These breeds are at high risk 
                of heat stroke and breathing failure in the pressurized cargo environment.
              </p>
            </div>

            <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 border border-green-100">
              <h3 className="text-xl font-bold text-green-800 mb-3 flex items-center gap-2">
                <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                Why Pre-Checking Your Crate Matters
              </h3>
              <p className="text-green-700 leading-relaxed">
                Airlines report rejection rates of 10-15% at check-in, primarily due to non-compliant crates 
                or missing documentation. With check-in occurring just hours before your flight, there's no 
                time to fix issues. Our AI-powered pre-check service analyzes your crate photos against 
                IATA and airline-specific requirements, identifying problems before you leave home.
              </p>
            </div>
          </div>
        </div>
      </section>
    </SEOLayout>
  );
};

export default AirlinesHub;
